import { useEffect, useState } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { api } from '../api';
import type {
  BaseRates,
  Landlord,
  LeaseInput,
  LeaseListRow,
  LeasePremiseUse,
  MarketingIndividual,
  Property,
  RentPeriod,
  Tenant,
} from '../types';

type Props = {
  initial?: LeaseListRow | null;
  properties: Property[];
  tenants: Tenant[];
  landlords: Landlord[];
  marketingIndividuals: MarketingIndividual[];
  premiseUses: LeasePremiseUse[];
  onSubmit: (input: LeaseInput) => Promise<void> | void;
  onCancel: () => void;
  submitLabel: string;
};

type FormShape = {
  propertyId: string;
  tenantId: string;
  landlordId: string;
  marketingIndividualId: string;
  leasePremiseUseId: string;
  unit: string;
  sageCode: string; // Sage customer account for this lease
  rentalCurrency: string;
  status: string;
  paidStatus: string;
  boDate: string;
  firstInvoiceDate: string;
  commencementDate: string;
  executionDate: string;
  expirationDate: string;
  beneficialOccupationPeriod: string;
  leaseDurationMonths: string;
  landlordAllowable: string;
  escalationPct: string; // %; converted to fraction on submit
  includeBozInflationInEscalation: string; // '', 'true', 'false'
  externalRate: string;
  externalSqm: string;
  internalRate: string;
  internalSqm: string;
  mezzanineRate: string;
  mezzanineSqm: string;
  miscRate: string;
  miscSqm: string;
  umbrellaRate: string;
  umbrellaSqm: string;
  coveredAreaRate: string;
  coveredAreaSqm: string;
  storeRoomRate: string;
  storeRoomSqm: string;
  balconyRate: string;
  balconySqm: string;
  depositBankGuarantee: string;
  leaseRegistrationFee: string;
  managementFee: string; // absolute amount in the lease currency
  guarantorDetails: string;
  leaseAddendum: string;
  leaseAddendums: string; // '', 'true', 'false'
  notes: string;
};

const fromFraction = (v: string | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  return (n * 100).toString();
};
const toFraction = (v: string): string | null => {
  if (v.trim() === '') return null;
  const n = Number(v) / 100;
  if (!Number.isFinite(n)) return null;
  return n.toFixed(4);
};
const idOrNull = (v: string): number | null => {
  if (v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
const numOrNull = (v: string): string | null => (v.trim() === '' ? null : v);
const intOrNull = (v: string): number | null => {
  if (v.trim() === '') return null;
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};
const triBool = (v: string): boolean | null => {
  if (v === 'true') return true;
  if (v === 'false') return false;
  return null;
};

const CURRENT_RATE_FIELDS: { label: string; key: keyof LeaseListRow }[] = [
  { label: 'External', key: 'externalRate' },
  { label: 'Internal', key: 'internalRate' },
  { label: 'Mezzanine', key: 'mezzanineRate' },
  { label: 'Misc', key: 'miscRate' },
  { label: 'Umbrella', key: 'umbrellaRate' },
  { label: 'Covered area', key: 'coveredAreaRate' },
  { label: 'Store room', key: 'storeRoomRate' },
  { label: 'Balcony', key: 'balconyRate' },
];

// Compact "External 12.50 · Internal 8.00" summary of the synced cache rates.
const currentRatesSummary = (lease: LeaseListRow): string =>
  CURRENT_RATE_FIELDS.map(({ label, key }) => ({ label, v: Number(lease[key] ?? 0) }))
    .filter((x) => Number.isFinite(x.v) && x.v !== 0)
    .map((x) => `${x.label} ${x.v.toFixed(2)}`)
    .join(' · ') || 'none';

const sourceLabel = (source: RentPeriod['source']): string =>
  source === 'base' ? 'Base' : source === 'escalation' ? 'Escalation' : 'Renewal reset';

const formatPct = (pct: string | null): string =>
  pct === null ? '—' : `${(Number(pct) * 100).toFixed(2)}%`;

const formatTotal = (total: string): string =>
  Number(total).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const LeaseForm = ({
  initial,
  properties,
  tenants,
  landlords,
  marketingIndividuals,
  premiseUses,
  onSubmit,
  onCancel,
  submitLabel,
}: Props) => {
  // The rate inputs edit the BASE rates (the schedule anchor). For leases
  // already carrying a base_rates vector we show that; otherwise fall back to
  // the cache columns (pre-backfill leases).
  const baseRate = (key: keyof BaseRates, fallback: string | null | undefined): string => {
    const v = initial?.baseRates?.[key];
    return v !== undefined && v !== null ? String(v) : (fallback ?? '');
  };

  const defaults: FormShape = {
    propertyId: initial?.propertyId?.toString() ?? '',
    tenantId: initial?.tenantId?.toString() ?? '',
    landlordId: initial?.landlordId?.toString() ?? '',
    marketingIndividualId: initial?.marketingIndividualId?.toString() ?? '',
    leasePremiseUseId: initial?.leasePremiseUseId?.toString() ?? '',
    unit: initial?.unit ?? '',
    sageCode: initial?.sageCode ?? '',
    rentalCurrency: initial?.rentalCurrency ?? '',
    status: initial?.status ?? 'Active',
    paidStatus: initial?.paidStatus ?? '',
    boDate: initial?.boDate ?? '',
    firstInvoiceDate: initial?.firstInvoiceDate ?? '',
    commencementDate: initial?.commencementDate ?? '',
    executionDate: initial?.executionDate ?? '',
    expirationDate: initial?.expirationDate ?? '',
    beneficialOccupationPeriod: initial?.beneficialOccupationPeriod?.toString() ?? '',
    leaseDurationMonths: initial?.leaseDurationMonths?.toString() ?? '',
    landlordAllowable: initial?.landlordAllowable?.toString() ?? '',
    escalationPct: fromFraction(initial?.escalation),
    includeBozInflationInEscalation:
      initial?.includeBozInflationInEscalation === true
        ? 'true'
        : initial?.includeBozInflationInEscalation === false
          ? 'false'
          : '',
    externalRate: baseRate('external_rate', initial?.externalRate),
    externalSqm: initial?.externalSqm ?? '',
    internalRate: baseRate('internal_rate', initial?.internalRate),
    internalSqm: initial?.internalSqm ?? '',
    mezzanineRate: baseRate('mezzanine_rate', initial?.mezzanineRate),
    mezzanineSqm: initial?.mezzanineSqm ?? '',
    miscRate: baseRate('misc_rate', initial?.miscRate),
    miscSqm: initial?.miscSqm ?? '',
    umbrellaRate: baseRate('umbrella_rate', initial?.umbrellaRate),
    umbrellaSqm: initial?.umbrellaSqm ?? '',
    coveredAreaRate: baseRate('covered_area_rate', initial?.coveredAreaRate),
    coveredAreaSqm: initial?.coveredAreaSqm ?? '',
    storeRoomRate: baseRate('store_room_rate', initial?.storeRoomRate),
    storeRoomSqm: initial?.storeRoomSqm ?? '',
    balconyRate: baseRate('balcony_rate', initial?.balconyRate),
    balconySqm: initial?.balconySqm ?? '',
    depositBankGuarantee: initial?.depositBankGuarantee ?? '',
    leaseRegistrationFee: initial?.leaseRegistrationFee ?? '',
    managementFee: initial?.managementFee ?? '',
    guarantorDetails: initial?.guarantorDetails ?? '',
    leaseAddendum: initial?.leaseAddendum ?? '',
    leaseAddendums:
      initial?.leaseAddendums === true ? 'true' : initial?.leaseAddendums === false ? 'false' : '',
    notes: initial?.notes ?? '',
  };

  const { register, handleSubmit, formState, watch } = useForm<FormShape>({ defaultValues: defaults });
  const hasAddendums = watch('leaseAddendums');

  // Read-only rent schedule for existing leases (derived data; manual
  // escalations are not possible).
  const [schedule, setSchedule] = useState<RentPeriod[] | null>(null);
  const leaseId = initial?.id;
  useEffect(() => {
    if (!leaseId) return;
    let cancelled = false;
    api
      .leaseRentSchedule(leaseId)
      .then((rows) => {
        if (!cancelled) setSchedule(rows);
      })
      .catch(() => {
        if (!cancelled) setSchedule(null);
      });
    return () => {
      cancelled = true;
    };
  }, [leaseId]);

  const submit: SubmitHandler<FormShape> = async (data) => {
    const input: LeaseInput = {
      propertyId: idOrNull(data.propertyId),
      tenantId: idOrNull(data.tenantId),
      landlordId: idOrNull(data.landlordId),
      marketingIndividualId: idOrNull(data.marketingIndividualId),
      leasePremiseUseId: idOrNull(data.leasePremiseUseId),
      unit: data.unit || null,
      sageCode: data.sageCode.trim().toUpperCase() || null,
      rentalCurrency:
        data.rentalCurrency === 'USD' || data.rentalCurrency === 'ZMW' ? data.rentalCurrency : null,
      status: (data.status as LeaseInput['status']) ?? 'Active',
      paidStatus:
        data.paidStatus === 'Paid' || data.paidStatus === 'Unpaid' ? data.paidStatus : null,
      boDate: data.boDate || null,
      firstInvoiceDate: data.firstInvoiceDate || null,
      commencementDate: data.commencementDate || null,
      executionDate: data.executionDate || null,
      expirationDate: data.expirationDate || null,
      beneficialOccupationPeriod: intOrNull(data.beneficialOccupationPeriod),
      leaseDurationMonths: intOrNull(data.leaseDurationMonths),
      landlordAllowable: intOrNull(data.landlordAllowable),
      escalation: toFraction(data.escalationPct),
      includeBozInflationInEscalation: triBool(data.includeBozInflationInEscalation),
      externalRate: numOrNull(data.externalRate),
      externalSqm: numOrNull(data.externalSqm),
      internalRate: numOrNull(data.internalRate),
      internalSqm: numOrNull(data.internalSqm),
      mezzanineRate: numOrNull(data.mezzanineRate),
      mezzanineSqm: numOrNull(data.mezzanineSqm),
      miscRate: numOrNull(data.miscRate),
      miscSqm: numOrNull(data.miscSqm),
      umbrellaRate: numOrNull(data.umbrellaRate),
      umbrellaSqm: numOrNull(data.umbrellaSqm),
      coveredAreaRate: numOrNull(data.coveredAreaRate),
      coveredAreaSqm: numOrNull(data.coveredAreaSqm),
      storeRoomRate: numOrNull(data.storeRoomRate),
      storeRoomSqm: numOrNull(data.storeRoomSqm),
      balconyRate: numOrNull(data.balconyRate),
      balconySqm: numOrNull(data.balconySqm),
      depositBankGuarantee: numOrNull(data.depositBankGuarantee),
      leaseRegistrationFee: numOrNull(data.leaseRegistrationFee),
      managementFee: numOrNull(data.managementFee),
      guarantorDetails: data.guarantorDetails || null,
      leaseAddendum: data.leaseAddendum || null,
      leaseAddendums: triBool(data.leaseAddendums),
      notes: data.notes || null,
    };
    await onSubmit(input);
  };

  return (
    <form
      id="lease-form"
      noValidate
      className={formState.isSubmitted ? 'submitted' : ''}
      onSubmit={(e) => {
        void handleSubmit(submit)(e);
      }}
    >
      <div className="form-grid">
        <div className="field">
          <label>Property</label>
          <select {...register('propertyId', { required: true })}>
            <option value="">—</option>
            {properties.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Tenant</label>
          <select {...register('tenantId', { required: true })}>
            <option value="">—</option>
            {tenants.map((t) => (
              <option key={t.id} value={t.id}>
                {t.companyName}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Landlord</label>
          <select {...register('landlordId', { required: true })}>
            <option value="">—</option>
            {landlords.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Marketing individual</label>
          <select {...register('marketingIndividualId', { required: true })}>
            <option value="">—</option>
            {marketingIndividuals.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Premise use</label>
          <select {...register('leasePremiseUseId', { required: true })}>
            <option value="">—</option>
            {premiseUses.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {!p.active ? ' (legacy)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Unit</label>
          <input {...register('unit', { required: true })} />
        </div>
        <div className="field">
          <label>Sage code (this lease)</label>
          <input placeholder="AP####" {...register('sageCode')} />
          <span className="muted" style={{ fontSize: 12 }}>
            Sage customer account this lease is invoiced under. A tenant can hold different codes
            for different shops; leases billed together share one code.
          </span>
        </div>
        <div className="field">
          <label>Status</label>
          <select {...register('status', { required: true })}>
            <option value="Draft">Draft</option>
            <option value="Active">Active</option>
            <option value="Terminated Early">Terminated Early</option>
            <option value="Expired">Expired</option>
          </select>
        </div>
        <div className="field">
          <label>Paid status</label>
          <select {...register('paidStatus', { required: true })}>
            <option value="">—</option>
            <option value="Paid">Paid</option>
            <option value="Unpaid">Unpaid</option>
          </select>
        </div>
        <div className="field">
          <label>Rental currency</label>
          <select {...register('rentalCurrency', { required: true })}>
            <option value="">—</option>
            <option value="USD">USD</option>
            <option value="ZMW">ZMW</option>
          </select>
        </div>
        <div className="field">
          <label>BO date</label>
          <input type="date" {...register('boDate', { required: true })} />
        </div>
        <div className="field">
          <label>First invoice date (escalation anniversary — leave empty to use BO date + BO period)</label>
          <input type="date" {...register('firstInvoiceDate')} />
        </div>
        <div className="field">
          <label>Commencement date</label>
          <input type="date" {...register('commencementDate', { required: true })} />
        </div>
        <div className="field">
          <label>Execution date</label>
          <input type="date" {...register('executionDate', { required: true })} />
        </div>
        <div className="field">
          <label>Expiration date</label>
          <input type="date" {...register('expirationDate', { required: true })} />
        </div>

        <div className="field">
          <label>Beneficial occupation period (days)</label>
          <input type="number" {...register('beneficialOccupationPeriod', { required: true })} />
        </div>
        <div className="field">
          <label>Lease duration months</label>
          <input type="number" {...register('leaseDurationMonths', { required: true })} />
        </div>
        <div className="field">
          <label>Landlord allowable</label>
          <input type="number" {...register('landlordAllowable', { required: true })} />
        </div>
        <div className="field">
          <label>Escalation (%)</label>
          <input type="number" step="0.01" {...register('escalationPct', { required: true })} />
        </div>
        <div className="field">
          <label>Include BOZ inflation in escalation</label>
          <select {...register('includeBozInflationInEscalation', { required: true })}>
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
        <div className="field">
          <label>Management fee (amount)</label>
          <input type="number" step="0.01" {...register('managementFee', { required: true })} />
        </div>

        <h3 className="form-section full">
          Base rates (schedule anchor — current rent is computed from these)
        </h3>
        {initial?.baseRates && (
          <div className="field full">
            <span className="muted" style={{ fontSize: 12 }}>
              These are the base rates as at {initial.baseRatesDate ?? 'the schedule anchor date'};
              the rent schedule escalates them automatically. Current effective rates:{' '}
              {currentRatesSummary(initial)}.
            </span>
          </div>
        )}
        <div className="field">
          <label>External rate</label>
          <input type="number" step="0.01" {...register('externalRate', { required: true })} />
        </div>
        <div className="field">
          <label>External sqm</label>
          <input type="number" step="0.01" {...register('externalSqm', { required: true })} />
        </div>
        <div className="field">
          <label>Internal rate</label>
          <input type="number" step="0.01" {...register('internalRate', { required: true })} />
        </div>
        <div className="field">
          <label>Internal sqm</label>
          <input type="number" step="0.01" {...register('internalSqm', { required: true })} />
        </div>
        <div className="field">
          <label>Mezzanine rate</label>
          <input type="number" step="0.01" {...register('mezzanineRate', { required: true })} />
        </div>
        <div className="field">
          <label>Mezzanine sqm</label>
          <input type="number" step="0.01" {...register('mezzanineSqm', { required: true })} />
        </div>
        <div className="field">
          <label>Misc rate</label>
          <input type="number" step="0.01" {...register('miscRate', { required: true })} />
        </div>
        <div className="field">
          <label>Misc sqm</label>
          <input type="number" step="0.01" {...register('miscSqm', { required: true })} />
        </div>
        <div className="field">
          <label>Umbrella rate</label>
          <input type="number" step="0.01" {...register('umbrellaRate', { required: true })} />
        </div>
        <div className="field">
          <label>Umbrella sqm</label>
          <input type="number" step="0.01" {...register('umbrellaSqm', { required: true })} />
        </div>
        <div className="field">
          <label>Covered area rate</label>
          <input type="number" step="0.01" {...register('coveredAreaRate')} />
        </div>
        <div className="field">
          <label>Covered area sqm</label>
          <input type="number" step="0.01" {...register('coveredAreaSqm')} />
        </div>
        <div className="field">
          <label>Store room rate</label>
          <input type="number" step="0.01" {...register('storeRoomRate')} />
        </div>
        <div className="field">
          <label>Store room sqm</label>
          <input type="number" step="0.01" {...register('storeRoomSqm')} />
        </div>
        <div className="field">
          <label>Balcony rate</label>
          <input type="number" step="0.01" {...register('balconyRate')} />
        </div>
        <div className="field">
          <label>Balcony sqm</label>
          <input type="number" step="0.01" {...register('balconySqm')} />
        </div>

        {leaseId && schedule && schedule.length > 0 && (
          <>
            <h3 className="form-section full">Rent schedule</h3>
            <div className="field full">
              <div className="table-wrap">
                <table className="grid">
                  <thead>
                    <tr>
                      <th align="left">Period start</th>
                      <th align="left">Source</th>
                      <th align="right">Escalation %</th>
                      <th align="right">#</th>
                      <th align="right">Monthly total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((p) => (
                      <tr key={p.id} style={p.isCurrent ? { fontWeight: 600 } : undefined}>
                        <td>{p.periodStart}</td>
                        <td>{sourceLabel(p.source)}</td>
                        <td align="right">{formatPct(p.escalationPct)}</td>
                        <td align="right">{p.escalationNumber ?? '—'}</td>
                        <td align="right">{formatTotal(p.monthlyTotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <span className="muted" style={{ fontSize: 12 }}>
                Derived from the base rates and BO-anchored escalation dates; the bold row is the
                current period. Rates cannot be escalated manually.
              </span>
            </div>
          </>
        )}

        <h3 className="form-section full">Fees &amp; deposits</h3>
        <div className="field">
          <label>Deposit / bank guarantee</label>
          <input type="number" step="0.01" {...register('depositBankGuarantee', { required: true })} />
        </div>
        <div className="field">
          <label>Lease registration fee</label>
          <input type="number" step="0.01" {...register('leaseRegistrationFee', { required: true })} />
        </div>

        <div className="field full">
          <label>Guarantor details</label>
          <input {...register('guarantorDetails', { required: true })} />
        </div>

        <div className="field">
          <label>Has addendums?</label>
          <select {...register('leaseAddendums', { required: true })}>
            <option value="">—</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
        {hasAddendums === 'true' && (
          <div className="field">
            <label>Addendum (free text)</label>
            <input {...register('leaseAddendum', { required: true })} />
          </div>
        )}

        <div className="field full">
          <label>Notes</label>
          <textarea rows={4} {...register('notes')} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={formState.isSubmitting}>
          {submitLabel}
        </button>
      </div>
    </form>
  );
};
